import { dayInMilliseconds, hourInMilliseconds, minuteInMilliseconds } from "@vmosolutions/oc-interfaces";
import * as _ from "lodash";
import * as moment from "moment-timezone";

export function omit<T extends object, K extends keyof T>(target: T, ...omitKeys: K[]): Omit<T, K> {
  return (Object.keys(target) as K[]).reduce((res, key) => {
    if (!omitKeys.includes(key)) {
      res[key] = target[key];
    }
    return res;
  }, {} as any);
}

export function dropRight(arr: any[], numToDrop = 1): any[] {
  if (arr.length === 0 || arr.length <= numToDrop) {
    return [];
  }

  const length = arr.length - numToDrop;
  const newArr = new Array(length);
  for (let i = 0; i < length; i++) {
    newArr[i] = arr[i];
  }

  return newArr;
}

interface IIsoTest {
  type: string;
  value: string;
  valid: boolean;
  error: string;
}

//Convert HH:mm to milseconds
export const hrsMin2Milli = (time: string): number => {
  if (!time) return 0;
  const [hours, minutes] = time.split(":");
  return parseInt(hours) * hourInMilliseconds + parseInt(minutes) * minuteInMilliseconds;
};

//Convert HH:mm to milseconds
export const DaysHrsMin2Milli = (time: string): number => {
  if (!time) return 0;
  const [dayshours, minutes] = time.split(":");
  const [days, hours] = dayshours.split("+");
  return (
    parseInt(days) * dayInMilliseconds + parseInt(hours) * hourInMilliseconds + parseInt(minutes) * minuteInMilliseconds
  );
};
// Convert milliseconds to D+HH:mm
export function milli2DaysHrsMin(milliseconds: number, padHours?: true): string | undefined {
  const duration = moment.duration(milliseconds, "millisecond");
  const isNegative = milliseconds < 0 ? "-" : "";
  if (!isNaN(duration?.days()) && !isNaN(duration?.hours()) && !isNaN(duration?.minutes())) {
    const hours = padHours
      ? Math.abs(duration.hours()).toString().padStart(2, "0")
      : Math.abs(duration.hours()).toString();
    const mins = Math.abs(duration.minutes()).toString().padStart(2, "0");
    //!! please dont change this to duration.days() unless you are going to show the # of months
    const days = Math.floor(duration.asDays()) > 0 ? Math.floor(Math.abs(duration.asDays())).toString() : undefined;
    return `${isNegative}${days ? `${days}+` : ""}${hours}:${mins}`;
  }
  return undefined;
}

// Convert Minutes to HH:mm
export function minutes2HrsMin(minutes: number, padHours?: true): string | undefined {
  const duration = moment.duration(minutes, "minutes");
  const isNegative = minutes < 0 ? "-" : "";
  if (duration?.hours && duration?.minutes) {
    const hours = padHours
      ? Math.abs(duration.hours()).toString().padStart(2, "0")
      : Math.abs(duration.hours()).toString();
    const mins = Math.abs(duration.minutes()).toString().padStart(2, "0");
    return `${isNegative}${hours}:${mins}`;
  }
  return undefined;
}

// Returns true if value of a valid epoch format.
export const isEpochDate = (value: number | string): boolean => {
  return typeof value === "number" && new Date(value)?.valueOf() === value;
};

export function isIsoDate(DateStr: string, strictCheck?: boolean): IIsoTest {
  const looseRegex = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/;
  const strictRegex =
    /^(-?(?:[1-9][0-9]*)?[0-9]{4})-(1[0-2]|0[1-9])-(3[01]|0[1-9]|[12][0-9])T(2[0-3]|[01][0-9]):([0-5][0-9]):([0-5][0-9])(.[0-9]+)?(Z)?$/g;
  if ((strictCheck && !strictRegex.test(DateStr)) || !looseRegex.test(DateStr)) {
    return {
      type: "unknown",
      value: DateStr,
      valid: false,
      error: "incorrect format",
    };
  }
  const d = new Date(DateStr);
  // Check if date is valid before returning it.
  if (Object.prototype.toString.call(d) === "[object Date]") {
    // it is a date
    if (isNaN(d?.valueOf())) {
      // const date = DateStr.slice(0, 10);
      // const hours = DateStr.slice(11, 16);
      // const seconds = DateStr.slice(17, 19);
      // const utc = DateStr.slice(20, 24);
      // Debug.log(date + ' ' + hours + ' ' + seconds + ' ' + utc);
      return {
        type: "isoString",
        value: DateStr,
        valid: false,
        error: "incorrect format",
      };
    } else {
      return {
        type: "isoString",
        value: DateStr,
        valid: d.toISOString() === DateStr,
        error: "",
      };
    }
  } else {
    return {
      type: "unknown",
      value: DateStr,
      valid: false,
      error: "incorrect format",
    };
  }
}

// Pads a number with leading zeros to return a given width.
export function padNumber(number: number, width: number): string {
  if (typeof number === "string") {
    return number as string;
  }
  const numberExceedsAllowedWidth =
    (number > 9 && width === 2) || (number > 99 && width === 3) || (number > 999 && width === 4) || number > 9999;
  return numberExceedsAllowedWidth
    ? number.toString() // if incompatible, dont pad.
    : new Array(+width + 1 - (number + "").length).join("0") + number;
}

export function convertDateObjectToString(DateObject: Date, asIso?: boolean, hideTime?: boolean): string {
  const weekday = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  let newDate: string;
  if (!asIso) {
    const day = weekday[DateObject.getUTCDay()];
    const date = DateObject.getUTCDate();
    const month = months[DateObject.getUTCMonth()];
    const year = DateObject.getUTCFullYear();
    const hours = padNumber(DateObject.getUTCHours(), 2);
    const minutes = padNumber(DateObject.getUTCMinutes(), 2);
    if (!hideTime) {
      newDate = day + ", " + date + " " + month + " " + year + " " + hours + ":" + minutes;
    } else {
      newDate = day + ", " + date + " " + month + " " + year;
    }
  } else {
    const isoString = DateObject.toISOString();
    const locT: number = isoString.indexOf("T");
    const date = isoString.substring(0, locT);
    const hours = padNumber(DateObject.getUTCHours(), 2);
    const minutes = padNumber(DateObject.getUTCMinutes(), 2);
    if (!hideTime) {
      newDate = date + " " + hours + ":" + minutes + "Z";
    } else {
      newDate = date;
    }
  }
  return newDate;
}

export function checkOS(): string {
  const OS =
    window.navigator.platform.indexOf("Mac") != -1
      ? "macOS"
      : window.navigator.platform.indexOf("Win") != -1
        ? "Windows"
        : "Linux";
  return OS;
}

export function detectTrackPad(event: any): boolean {
  return event.deltaX !== 0 && event.deltaY !== 0;
}

//Events are added so the above function works.
window.addEventListener("wheel", detectTrackPad, { passive: true });

export function checkBrowser(): string {
  const c = navigator.userAgent.search("Chrome");
  const f = navigator.userAgent.search("Firefox");
  const m8 = navigator.userAgent.search("MSIE 8.0");
  const m9 = navigator.userAgent.search("MSIE 9.0");
  let browser = "";
  if (c > -1) {
    browser = "Chrome";
  } else if (f > -1) {
    browser = "Firefox";
  } else if (m9 > -1) {
    browser = "MSIE 9.0";
  } else if (m8 > -1) {
    browser = "MSIE 8.0";
  }
  return browser;
}

export function hexToRgbA(hex: string, alpha: number): string {
  let c: any;
  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    c = hex.substring(1).split("");
    if (c.length === 3) {
      c = [c[0], c[0], c[1], c[1], c[2], c[2]];
    }
    c = "0x" + c.join("");
    return "rgba(" + [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(",") + "," + alpha + ")";
  }
  throw new Error("Bad Hex");
}

export function LightenDarkenColor(hex: string, lum: number): string {
  // validate hex string
  hex = String(hex).replace(/[^0-9a-f]/gi, "");
  if (hex.length < 6) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  lum = lum || 0;
  // convert to decimal and change luminosity
  let rgb = "#",
    c,
    i;
  for (i = 0; i < 3; i++) {
    c = parseInt(hex.substr(i * 2, 2), 16);
    c = Math.round(Math.min(Math.max(0, c + c * lum), 255)).toString(16);
    rgb += ("00" + c).substr(c.length);
  }
  return rgb;
}

export function mergeArrays(arrays: any[]): any[] {
  let jointArray: any[] = [];
  arrays.forEach((array) => {
    jointArray = [...jointArray, ...array];
  });
  return Array.from(new Set([...jointArray]));
}

export function isDevEnv(): boolean {
  return import.meta.env.MODE === "development";
}
// Small helper function which returns true if a string passed in is a valid number.
export function isNumber(n: string): boolean {
  return !isNaN(parseFloat(n)) && !isNaN((n as any) - 0);
}

/**
 * convertMilli2DaysHrsMin Converts milliseconds to UTC Days, Hours and Minutes
 * @param ms - the duration in ms to convert to human readable text
 */
export function convertMilli2DaysHrsMin(ms: number): string {
  const days = Math.floor(ms / (24 * 60 * 60 * 1000));
  const hours = Math.floor((ms % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
  const minutes = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));
  const dayString = days ? `${days} day${days > 1 ? "s" : ""}, ` : "";
  const hourString = hours ? `${hours} hour${hours > 1 ? "s" : ""} ` : "";
  const minutePrefix = days > 0 || hours > 0 ? " and" : "";
  const minuteString = minutes ? `${minutePrefix} ${minutes} minute${minutes > 1 ? "s" : ""}` : "";
  return dayString + hourString + minuteString;
}

/**
 * Deep diff between two object, using lodash
 * @param object - Object compared
 * @param base - Object to compare with
 * Return a new object representing only the changes values.
 */
export function getDeepDiff(object: any, base: any): any {
  function changes(object: any, base: any): any {
    return _.transform(object, function (result, value, key) {
      if (!_.isEqual(value, base[key])) {
        result[key] = _.isObject(value) && _.isObject(base[key]) ? changes(value, base[key]) : value;
      }
    });
  }
  return changes(object, base);
}

/**
 * Single level field comparison function.
 * @param values - Modified form values
 * @param initialValues - Initial form values
 * Return a new object representing only the changes values.
 */
//! Warning, this function will not work correctly if tracking nested fields as top level object will be returned in full.
export function getFormFieldDiff(values: any, initialValues: any): any {
  // Change object that will be returned if fields are deemed dirty
  const dirtyFields: { [key: string]: any } = {};
  //check that customFields has keys
  if (values && initialValues) {
    for (const [key, value] of Object.entries(values)) {
      const initialValue = initialValues[key];
      const newValue = values[key];
      if (!_.isEqual(newValue, initialValue) && key !== "__v") {
        dirtyFields[key] = value;
      }
    }
    return dirtyFields;
  }
  // If not enough info was given to run a comparison, just return values, even if null itself.
  return values;
}

/**
 * Function used to find the next valid focussable element inside a form.
 * @param input - active element (will use active focused element if not passed in)
 */
export const focusNextElement = (input?: HTMLInputElement): void => {
  // Add all elements we want to include in our selection
  const focussableElements =
    'a:not([disabled]), button:not([disabled]), input:not([disabled]), [tabindex]:not([disabled]):not([tabindex="-1"])';
  const activeElement: any = input ?? document?.activeElement;
  const focussableNodes = activeElement?.form?.querySelectorAll(focussableElements);
  if (focussableNodes?.length > 0) {
    // console.log(typeof focussableNodes);
    const nodes = Array.prototype.filter.call(focussableNodes, (element) => {
      return element.offsetWidth > 0 || element.offsetHeight > 0 || element === document.activeElement;
    });
    const index = nodes?.indexOf(document.activeElement);
    if (index > -1) {
      const nextElement = nodes[index + 1] || nodes[0];
      nextElement?.focus();
    }
  }
};

interface IValidateDateTimeResponse {
  value: string | number;
  valid: boolean;
  errorString: string;
  date?: { value: string; valid: boolean };
  time?: { value: string; valid: boolean };
  remainder?: { value: string; valid: boolean };
}

export const validateDateTimeFormat = (value: string | number): IValidateDateTimeResponse => {
  if (!value) {
    return { value: "", valid: true, errorString: "" };
  }

  if (isEpochDate(value)) {
    return { value: value, valid: true, errorString: "" };
  }

  const checkDate = isIsoDate(value as string);

  let errorStr = "";
  // ISO Date value is incorrect formatted (unknown issue)
  if (!checkDate.valid && checkDate.type === "unknown") {
    errorStr = "Date/time format incorrect.";
  } else if (!checkDate.valid && checkDate.type === "isoString") {
    // Format has been detected as ressembling an ISO string but a component is incorrect
    const dateStr = checkDate.value.slice(0, 10);
    const timeStr = checkDate.value.slice(11, 16);
    const remainderStr = checkDate.value.slice(17, 24);
    // Check Date
    const invalidDate = !/^\d{4}-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/.test(dateStr);
    const invalidTime = !/^(0[0-9]|1[0-9]|2[0-3]|[0-9]):[0-5][0-9]$/.test(timeStr);
    const invalidRemainder = !/^([0-5][0-9])(.[0-9]+)?(Z)?$/.test(remainderStr);

    switch (true) {
      case invalidDate:
        errorStr = "Date format incorrect.";
        break;
      case invalidTime:
        errorStr = "Time format incorrect.";
        break;
      case invalidRemainder:
        errorStr = "Timezone incorrect.";
        break;
      default:
      // do nothing
    }
    // Debug.log(invalidDate + ' ' + invalidTime + ' ' + invalidRemainder);
    // Debug.log(date + ' ' + hours + ' ' + remainder);
    return {
      value: value,
      valid: errorStr.length === 0,
      errorString: errorStr,
      date: { value: dateStr, valid: !invalidDate },
      time: { value: timeStr, valid: !invalidTime },
      remainder: { value: remainderStr, valid: !invalidRemainder },
    };
  }
  return { value: value, valid: errorStr.length === 0, errorString: errorStr };
};

const hasArrayBuffer = typeof ArrayBuffer === "function";

/**
 * Check if the given value is an ArrayBuffer.
 * @param value - The value to check.
 * @returns boolean Returns `true` if the given value is an ArrayBuffer, else `false`.
 * @example
 * isArrayBuffer(new ArrayBuffer()) - true
 * isArrayBuffer([]) - false
 */
export const isArrayBuffer = (value: unknown): boolean => {
  return (
    hasArrayBuffer && (value instanceof ArrayBuffer || Object.prototype.toString.call(value) === "[object ArrayBuffer]")
  );
};

/**
 * convert an array Buffer to string
 * @param buf - Uint8Array
 * @returns string
 */
export const ab82str = (buf: any): string => {
  //@ts-ignore
  // return String.fromCharCode.apply(null, new Uint16Array(buf));
  return String.fromCharCode.apply(null, new Uint8Array(buf));
};

/** Unflatted JSON object */
export function unflattenObj(data: any): any {
  const result: { [key: string]: any } = {};
  for (const i in data) {
    const keys = i.split(".");
    keys?.reduce(function (r, e, j) {
      return r[e] || (r[e] = isNaN(Number(keys[j + 1])) ? (keys.length - 1 === j ? data[i] : {}) : []);
    }, result);
  }
  return result;
}

/** Flatten JSON object */
export function flattenObj(obj: any, parent?: any, res: { [key: string]: any } = {}): any {
  for (const key in obj) {
    const propName = parent ? parent + "." + key : key;
    if (typeof obj[key] == "object") {
      flattenObj(obj[key], propName, res);
    } else {
      res[propName] = obj[key];
    }
  }
  return res;
}

/**
 * Deep diff between two object-likes
 * @param fromObject - the original object
 * @param toObject - the updated object
 * @returns - a new object which represents the diff
 */
export function deepDiff(fromObject: any, toObject: any): any {
  const changes: { [key: string]: any } = {};

  const buildPath = (path: any, obj: any, key: string): any => (_.isUndefined(path) ? key : `${path}.${key}`);

  const walk = (fromObject: any, toObject: any, path?: any): void => {
    for (const key of _.keys(fromObject)) {
      const currentPath = buildPath(path, fromObject, key);
      if (!_.has(toObject, key)) {
        changes[currentPath] = { from: _.get(fromObject, key) };
      }
    }

    for (const [key, to] of _.entries(toObject)) {
      const currentPath = buildPath(path, toObject, key);
      if (!_.has(fromObject, key)) {
        changes[currentPath] = { to };
      } else {
        const from = _.get(fromObject, key);
        if (!_.isEqual(from, to)) {
          if (_.isObjectLike(to) && _.isObjectLike(from)) {
            walk(from, to, currentPath);
          } else {
            changes[currentPath] = { from, to };
          }
        }
      }
    }
  };

  walk(fromObject, toObject);

  return changes;
}

/**
 * Compare two records carrying a `displayOrder`, ascending.
 *
 * Crew positions rank by displayOrder with the LOWEST value ranking highest (CA is 1), so sorting
 * ascending puts the highest-ranking entry first. Generic because the same comparator is wanted for
 * positions, bookings and anything else that carries the field.
 *
 * Note this does not copy - `Array.prototype.sort` still mutates in place, so call it on a copy
 * when the array comes from redux state.
 */
export function sortByDisplayOrder<T extends { displayOrder: number }>(a: T, b: T): number {
  return a.displayOrder - b.displayOrder;
}
